/**
 * QuoteProceduresSection.tsx
 *
 * The Procedures band: Category -> Sub Category -> Procedure, with the hours
 * and the two prices legacy shows (Normal and Effective).
 *
 * The gap between Normal and Effective IS the procedure discount — legacy
 * displays the difference in Totals but never subtracts it again, because the
 * discount is already inside the effective price.
 */
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';
import { getQuoteHourBands, type QuoteProcedure } from '@/services/quotes.service';
import { QuoteSectionCard, EmptySectionRow } from './QuoteSectionCard';
import { DraftNumberInput } from './DraftNumberInput';
import { AutoGrowTextarea } from '@/components/ui/Textarea';
import { bandFor } from '@/lib/procedureHours';

/**
 * The procedure catalog endpoints predate this screen and each names its id
 * field differently, so rows are read loosely and the id is resolved by trying
 * the candidates in turn rather than assuming one shape.
 */
type CatalogRow = Record<string, unknown>;

/**
 * Resolve a row's id, tolerating the spelling each endpoint happens to use.
 *
 * The match is case-INSENSITIVE, and that is the load-bearing part. These three
 * endpoints return raw Eloquent models, so their JSON keys are the legacy
 * column names with a capital ID — `procedureCategoryID`,
 * `procedureSubCategoryID`, `procedureSpecificID`. Nothing renames them:
 * V5Controller::successResponse only fixes UTF-8, and no middleware rewrites
 * keys. JS property lookup is case-sensitive, so camelCase candidates alone
 * missed every row, `idOf` returned null, every <option> rendered value="",
 * and the Category -> Sub Category -> Procedure cascade could never advance
 * past the first select.
 *
 * Fixing it here rather than camelCasing the API is deliberate:
 * memberships.service.ts already reads `procedureSpecificID` in that exact
 * spelling, so changing the response shape would break it.
 */
function idOf(row: CatalogRow, ...candidates: string[]): number | null {
  let lowered: Map<string, string> | null = null;

  for (const candidate of candidates) {
    let key: string | undefined = candidate;

    if (!(candidate in row)) {
      lowered ??= new Map(Object.keys(row).map((k) => [k.toLowerCase(), k]));
      key = lowered.get(candidate.toLowerCase());
    }

    if (key === undefined) continue;

    const value = row[key];
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value !== '' && !Number.isNaN(Number(value))) return Number(value);
  }

  return null;
}

function nameOf(row: CatalogRow): string {
  return typeof row.name === 'string' ? row.name : '';
}

function numberOf(row: CatalogRow, key: string): number {
  const value = row[key];
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value !== '' && !Number.isNaN(Number(value))) return Number(value);
  return 0;
}

interface Props {
  procedures: Partial<QuoteProcedure>[];
  /** Normal/Effective price are money fields legacy gates on user level. */
  pricingReadOnly?: boolean;
  /** The quote's facility — its rate scale is where Hours comes from. */
  facilityId?: number | null;
  readOnly: boolean;
  onChange: (procedures: Partial<QuoteProcedure>[]) => void;
}

export function QuoteProceduresSection({
  procedures, readOnly, pricingReadOnly, facilityId, onChange,
}: Props) {
  // One fetch for the band list; every row picks from the same scale.
  const { data: hourBands } = useQuery({
    queryKey: ['quote-hour-bands', facilityId],
    queryFn: () => getQuoteHourBands(facilityId as number),
    enabled: !!facilityId,
    staleTime: 300_000,
  });

  const { data: categories } = useQuery({
    queryKey: ['procedure-categories'],
    queryFn: () =>
      apiClient
        .get<ApiResponse<CatalogRow[]>>('/procedure-categories')
        .then((r) => r.data.data ?? []),
    staleTime: 300_000,
  });

  function patch(index: number, changes: Partial<QuoteProcedure>) {
    onChange(procedures.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  }

  function remove(index: number) {
    onChange(procedures.filter((_, i) => i !== index));
  }

  function add() {
    onChange([
      ...procedures,
      {
        categoryId: null, procId: null, procSubId: null,
        laymansTerms: '', price: 0, effectivePrice: 0,
        expNumHours: 0, anesthesiaFee: 0, operatingRoomFee: 0,
      },
    ]);
  }

  return (
    <QuoteSectionCard
      title="Procedures"
      columns={['Hours', 'Normal', 'Effective']}
      onAdd={readOnly ? undefined : add}
      addLabel="Add"
    >
      {procedures.length === 0 ? (
        <EmptySectionRow>No procedures on this quote.</EmptySectionRow>
      ) : (
        <div className="space-y-3">
          {procedures.map((row, index) => (
            <ProcedureRow
              key={row.planProcedureId ?? `new-${index}`}
              row={row}
              categories={(categories ?? []) as CatalogRow[]}
              readOnly={readOnly}
              pricingReadOnly={pricingReadOnly ?? false}
              hourBands={hourBands ?? []}
              onPatch={(changes) => patch(index, changes)}
              onRemove={() => remove(index)}
            />
          ))}
        </div>
      )}
    </QuoteSectionCard>
  );
}

function ProcedureRow({
  row, categories, readOnly, pricingReadOnly, hourBands, onPatch, onRemove,
}: {
  row: Partial<QuoteProcedure>;
  categories: CatalogRow[];
  readOnly: boolean;
  pricingReadOnly: boolean;
  hourBands: number[];
  onPatch: (changes: Partial<QuoteProcedure>) => void;
  onRemove: () => void;
}) {
  const { data: subcategories } = useQuery({
    queryKey: ['procedure-subcategories', row.categoryId],
    queryFn: () =>
      apiClient
        .get<ApiResponse<CatalogRow[]>>(`/procedure-categories/${row.categoryId}/subcategories`)
        .then((r) => r.data.data ?? []),
    enabled: !!row.categoryId,
    staleTime: 300_000,
  });

  const { data: specifics } = useQuery({
    queryKey: ['procedures-specific', row.procId],
    queryFn: () =>
      apiClient
        .get<ApiResponse<CatalogRow[]>>(`/procedure-subcategories/${row.procId}/procedures`)
        .then((r) => r.data.data ?? []),
    enabled: !!row.procId,
    staleTime: 300_000,
  });

  /** Picking a specific procedure seeds its catalog price, hours and fees. */
  function selectSpecific(value: string) {
    const id = value ? Number(value) : null;
    const chosen = (specifics ?? []).find(
      (s: CatalogRow) => idOf(s, 'procedureSpecificId', 'id') === id,
    );

    onPatch({
      procSubId: id,
      specificName: chosen ? nameOf(chosen) : null,
      ...(chosen
        ? {
            price: numberOf(chosen, 'feeToPatient'),
            effectivePrice: numberOf(chosen, 'feeToPatient'),
            expNumHours: bandFor(numberOf(chosen, 'expNumHours'), hourBands),
            anesthesiaFee: numberOf(chosen, 'anesthesiaFee'),
            operatingRoomFee: numberOf(chosen, 'operatingRoomFee'),
            laymansTerms:
              typeof chosen.laymansTerms === 'string' ? chosen.laymansTerms : (row.laymansTerms ?? ''),
          }
        : {}),
    });
  }

  const stored = Number(row.expNumHours ?? 0);
  const offScaleHours =
    stored > 0 && !hourBands.some((b) => b === stored) ? stored : null;

  const select = 'rounded-md border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100 w-full';
  const money = 'w-24 rounded-md border border-slate-300 px-2 py-1.5 text-sm text-right disabled:bg-slate-100';

  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="grid gap-2 md:grid-cols-3 max-md:grid-cols-1">
        <label className="text-xs text-slate-600">
          Category
          <select
            className={select}
            value={row.categoryId ?? ''}
            disabled={readOnly}
            onChange={(e) =>
              onPatch({
                categoryId: e.target.value ? Number(e.target.value) : null,
                // A new category invalidates what was chosen below it.
                procId: null,
                procSubId: null,
                specificName: null,
              })
            }
          >
            <option value="">Select one…</option>
            {categories.map((c) => {
              const id = idOf(c, 'procedureCategoryId', 'categoryId', 'id');
              return <option key={id ?? nameOf(c)} value={id ?? ''}>{nameOf(c)}</option>;
            })}
          </select>
        </label>

        <label className="text-xs text-slate-600">
          Sub Category
          <select
            className={select}
            value={row.procId ?? ''}
            disabled={readOnly || !row.categoryId}
            onChange={(e) =>
              onPatch({
                procId: e.target.value ? Number(e.target.value) : null,
                procSubId: null,
                specificName: null,
              })
            }
          >
            <option value="">Select one…</option>
            {(subcategories ?? []).map((s: CatalogRow) => {
              const id = idOf(s, 'procedureSubCategoryId', 'subcategoryId', 'id');
              return <option key={id ?? nameOf(s)} value={id ?? ''}>{nameOf(s)}</option>;
            })}
          </select>
        </label>

        <label className="text-xs text-slate-600">
          Procedure
          <select
            className={select}
            value={row.procSubId ?? ''}
            disabled={readOnly || !row.procId}
            onChange={(e) => selectSpecific(e.target.value)}
          >
            <option value="">Select one…</option>
            {(specifics ?? []).map((s: CatalogRow) => {
              const id = idOf(s, 'procedureSpecificId', 'id');
              return <option key={id ?? nameOf(s)} value={id ?? ''}>{nameOf(s)}</option>;
            })}
          </select>
        </label>
      </div>

      <div className="mt-2 grid gap-2 md:grid-cols-[1fr_auto_auto_auto_auto] md:items-end max-md:grid-cols-1">
        <label className="text-xs text-slate-600">
          Description
          <AutoGrowTextarea
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
            rows={2}
            value={row.laymansTerms ?? ''}
            disabled={readOnly}
            onChange={(e) => onPatch({ laymansTerms: e.target.value })}
          />
        </label>

        <label className="text-xs text-slate-600">
          Hours
          <select
            className={money}
            value={String(row.expNumHours ?? 0)}
            // Hours is a money control: the summed value is the key the
            // anaesthesia and OR fees are looked up by. Legacy gates it on the
            // lock alone, which leaves a sub-level-3 user able to reprice those
            // two lines from a dropdown — the same inconsistency as its split
            // treatment of Normal vs Effective price. Gated with the prices.
            disabled={readOnly || pricingReadOnly}
            onChange={(e) => onPatch({ expNumHours: Number(e.target.value) || 0 })}
          >
            <option value="0">0</option>
            {hourBands.map((b) => (
              <option key={b} value={String(b)}>{b.toFixed(2)}</option>
            ))}
            {/*
              A stored value that is not on the current scale — a legacy row, or
              one saved before the facility changed. Kept selectable and marked
              so it is visible rather than silently snapped to another band,
              which would reprice the quote on the next save.
            */}
            {offScaleHours !== null && (
              <option value={String(offScaleHours)}>{offScaleHours} (not on this scale)</option>
            )}
          </select>
        </label>

        <label className="text-xs text-slate-600">
          Normal
          <DraftNumberInput
            className={money}
            value={row.price ?? 0}
            disabled={readOnly || pricingReadOnly}
            onCommit={(price) => onPatch({ price })}
          />
        </label>

        <label className="text-xs text-slate-600">
          Effective
          <DraftNumberInput
            className={money}
            value={row.effectivePrice ?? 0}
            disabled={readOnly || pricingReadOnly}
            onCommit={(effectivePrice) => onPatch({ effectivePrice })}
          />
        </label>

        {!readOnly && (
          <button
            type="button"
            onClick={onRemove}
            className="mb-1 rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 touch-target-44 tap-feedback"
            aria-label="Remove procedure"
          >
            <Trash2 className="h-4 w-4 touch-target-icon" />
          </button>
        )}
      </div>
    </div>
  );
}
